<template>
  <div class="p2p-page narrow">
    <WizardHeader :project="project" :current="step" :furthest="furthest" :saving="saving" :renamable="isMember" @rename="rename" />
    <div v-if="error" class="p2p-alert bad">{{ error }}</div>
    <div v-if="loading" class="p2p-stack">
      <div class="p2p-skeleton" style="height:260px"></div>
    </div>
    <template v-else>
      <StepSource v-if="step === 'source'" :project="project" :member="isMember" @project="setProject" @go="go" @error="error = $event" />
      <StepFormats v-else-if="step === 'formats'" :project="project" :config="config" :workspace="workspace" @project="setProject" @go="go" @refresh="load" />
      <StepReview v-else-if="step === 'review'" :project="project" :config="config" :member="isMember" @go="go" @refresh="load" />
      <StepAccounts v-else-if="step === 'accounts'" :project="project" :config="config" :member="isMember" @project="setProject" @go="go" />
      <StepPublish v-else-if="step === 'publish'" :project="project" :config="config" :member="isMember" @go="go" />
    </template>
  </div>
</template>

<script>
import WizardHeader, { STEPS } from '../components/WizardHeader.vue';
import StepSource from '../components/steps/StepSource.vue';
import StepFormats from '../components/steps/StepFormats.vue';
import StepReview from '../components/steps/StepReview.vue';
import StepAccounts from '../components/steps/StepAccounts.vue';
import StepPublish from '../components/steps/StepPublish.vue';
import { get, post, patch, message } from '../api';

function readProof(key) { try { return localStorage.getItem(key); } catch (error) { return null; } }
function writeProof(key, value) { try { if (value) localStorage.setItem(key, value); } catch (error) { /* Storage unavailable. */ } }
function removeProof(key) { try { localStorage.removeItem(key); } catch (error) { /* Storage unavailable. */ } }

export default {
  name: 'Wizard',
  components: { WizardHeader, StepSource, StepFormats, StepReview, StepAccounts, StepPublish },
  data: () => ({ project: null, loading: false, saving: false, error: '' }),
  computed: {
    step() { return this.$route.params.step || 'source'; },
    projectId() { return this.$route.params.id ? Number(this.$route.params.id) : null; },
    config() { return this.$store.getters['publisher/config']; },
    workspace() { return this.$store.getters['publisher/workspace']; },
    isMember() { const user = this.$store.getters.currentUser; return !!(user && !user.is_anonymous); },
    furthest() {
      if (!this.project || !this.project.id) return 0;
      if (this.project.batches && this.project.batches.length) return STEPS.length - 1;
      return this.project.knowledge_ready ? 1 : 0;
    }
  },
  watch: {
    projectId: { immediate: true, handler() { this.load(); } },
    step() { if (this.projectId) this.load(); }
  },
  async created() {
    // Steps fall back to built-in defaults until the configuration loads.
    await this.$store.dispatch('publisher/loadConfig').catch(() => {});
  },
  methods: {
    async load() {
      this.error = '';
      if (!this.projectId) { this.project = null; return; }
      if (!this.project || this.project.id !== this.projectId) this.loading = true;
      const key = 'p2p-guest:/projects/' + this.projectId;
      const user = this.$store.getters.currentUser;
      let claimError = null;
      try {
        const proof = readProof(key);
        if (proof && user && !user.is_anonymous) {
          // A sample made as a guest moves to the account that signed in on this browser.
          try { await post('/projects/' + this.projectId + '/claim', { guest_token: proof }, { skipAuthRecovery: true }); }
          catch (error) { claimError = error; }
        }
        try {
          this.project = await get('/projects/' + this.projectId);
        } catch (error) {
          throw claimError || error;
        }
        if (user && user.is_anonymous) writeProof(key, this.$store.state.token);
        else removeProof(key);
      } catch (error) {
        this.error = message(error);
      } finally {
        this.loading = false;
      }
    },
    setProject(project) { this.project = project; },
    go(step, query) {
      const id = (this.project && this.project.id) || this.projectId;
      this.$router.push({ path: '/create/' + id + '/' + step, query: query || {} }).catch(() => {});
    },
    async rename(name) {
      this.saving = true;
      try {
        this.project = await patch('/projects/' + this.project.id, { name });
      } catch (error) {
        this.error = message(error);
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
